import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { useNotification } from '@/shared/context/NotificationContext';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { collection, addDoc, query, getDocs, orderBy, updateDoc, doc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, ContentLanguageCode } from '@/shared/constants';
import {
    buildExhibitionColorMap,
    buildExhibitionColorOptions,
    getDefaultExhibitionColor,
} from '@/features/exhibitions/utils/exhibitionColors';
import { uploadImageToStorage } from '@/shared/utils/imageUtils';
import { logger } from '@/shared/utils/logger';
import { sortLanguagesWithFrFirst, getMuseumLanguagesAsContentCodes } from '@/shared/utils/languageUtils';
import { validateExhibition } from '@/shared/utils/validation';
import { handleFirebaseError } from '@/shared/services/firebase/errors';
import arrowIcon from '@/assets/svg/arrow_icon.svg';
import { LanguageTabs } from '../components/LanguageTabs';
import { ExhibitionFormFields } from '../components/ExhibitionFormFields';
import { LanguageModal } from '../components/LanguageModal';
import { Exhibition } from '@/shared/types';
import { buildImageAlt, findLanguagesMissingImageAlt } from '@/features/exhibitions/utils/imageAlt';

export const CreateExhibitionPage: React.FC = () => {
    const { t } = useTranslation();
    const { showError } = useNotification();
    const { museum, museumSlug } = useMuseum();
    const navigate = useNavigate();
    const { museumSlug: museumSlugParam } = useParams<{ museumSlug?: string }>();

    // Récupérer les langues du musée
    const museumLanguages = getMuseumLanguagesAsContentCodes(museum);

    const exhibitionColorOptions = buildExhibitionColorOptions(museum?.availableExhibitionColors);
    const effectiveAvailableColors = exhibitionColorOptions.map((option) => option.key);
    const exhibitionColorMap = buildExhibitionColorMap(exhibitionColorOptions);

    // À la création, si la couleur par défaut n'est pas dans la liste du musée, prendre la première disponible
    useEffect(() => {
        if (effectiveAvailableColors.length > 0 && !effectiveAvailableColors.includes(color)) {
            setColor(effectiveAvailableColors[0]);
        }
    }, [effectiveAvailableColors.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps -- initial only

    // Initialiser le statut par langue avec les langues du musée
    const initialStatusByLanguage: Record<string, 'draft' | 'published'> = {};
    museumLanguages.forEach((lang) => {
        initialStatusByLanguage[lang] = 'draft';
    });
    const [statusByLanguage, setStatusByLanguage] = useState<Record<string, 'draft' | 'published'>>(initialStatusByLanguage);
    const [color, setColor] = useState<string>(() => getDefaultExhibitionColor(exhibitionColorOptions));
    const [order, setOrder] = useState<number>(1);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
    const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
    const [languageModalOpen, setLanguageModalOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<ContentLanguageCode>('en');
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const [allExhibitions, setAllExhibitions] = useState<Exhibition[]>([]);

    // Gestion des langues et traductions
    const [activeLanguages, setActiveLanguages] = useState<ContentLanguageCode[]>(museumLanguages.length > 0 ? [museumLanguages[0]] : ['fr']);
    const [currentLanguage, setCurrentLanguage] = useState<ContentLanguageCode>(museumLanguages.length > 0 ? museumLanguages[0] : 'fr');

    // Initialiser les traductions avec les langues du musée
    const initialTranslations: Record<ContentLanguageCode, { title: string; description: string; coverAlt?: string }> = {} as Record<ContentLanguageCode, { title: string; description: string; coverAlt?: string }>;
    museumLanguages.forEach((lang) => {
        initialTranslations[lang] = { title: '', description: '' };
    });
    const [translations, setTranslations] = useState<Record<ContentLanguageCode, { title: string; description: string; coverAlt?: string }>>(initialTranslations);


    // Charger toutes les expositions pour connaître le total
    useEffect(() => {
        const loadAllExhibitions = async () => {
            if (!museum?.id) return;

            const db = getFirebaseDb();
            if (!db) return;

            try {
                const exhibitionsRef = collection(db, FIRESTORE_COLLECTIONS.MUSEUMS, museum.id, FIRESTORE_COLLECTIONS.EXHIBITIONS);
                const q = query(exhibitionsRef, orderBy('order', 'asc'));
                const querySnapshot = await getDocs(q);
                const exhibitionsData = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Exhibition[];

                setAllExhibitions(exhibitionsData);

                // Définir l'ordre par défaut (dernier + 1)
                if (exhibitionsData.length > 0) {
                    const lastOrder = exhibitionsData[exhibitionsData.length - 1].order || 0;
                    setOrder(lastOrder + 1);
                } else {
                    setOrder(1);
                }
            } catch (error) {
                logger.error('Erreur lors de la récupération des expositions:', error);
            }
        };

        loadAllExhibitions();
    }, [museum]);

    // Fermer les dropdowns quand on clique en dehors
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.dropdown')) {
                setStatusDropdownOpen(false);
                setColorDropdownOpen(false);
                setOrderDropdownOpen(false);
            }
            if (!target.closest('.language-dropdown')) {
                setLanguageDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageDelete = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleSaveDraft = async () => {
        if (!museum?.id) {
            showError(t('exhibitions.museumNotFound'));
            return;
        }

        setLoading(true);
        const db = getFirebaseDb();
        if (!db) {
            showError(t('exhibitions.databaseError'));
            setLoading(false);
            return;
        }

        try {
            // Préparer les traductions (name et description par langue)
            const nameTranslations: Record<string, string> = {};
            const descriptionTranslations: Record<string, string> = {};

            // Préparer le statut par langue
            const statusByLang: Record<string, 'draft' | 'published'> = {};
            activeLanguages.forEach((lang) => {
                nameTranslations[lang] = translations[lang]?.title || '';
                descriptionTranslations[lang] = translations[lang]?.description || '';
                statusByLang[lang] = statusByLanguage[lang] || 'draft';
            });

            // Valider les données avant sauvegarde (on valide avec le statut de la première langue pour la compatibilité)
            const validation = validateExhibition({
                name: nameTranslations,
                description: descriptionTranslations,
                status: statusByLang,
            });

            if (!validation.isValid) {
                validation.errors.forEach((error) => showError(error));
                setLoading(false);
                return;
            }

            const coverAltByLang = Object.fromEntries(
                activeLanguages.map((lang) => [lang, translations[lang]?.coverAlt || ''])
            );
            const missingAlt = findLanguagesMissingImageAlt({
                languages: activeLanguages,
                hasImage: Boolean(selectedImage),
                imageAlt: coverAltByLang,
                status: statusByLang,
            });
            if (missingAlt.length > 0) {
                showError(t('exhibitions.coverAlt.missing', { languages: missingAlt.map((lang) => lang.toUpperCase()).join(', ') }));
                setLoading(false);
                return;
            }

            // Créer l'exposition d'abord (sans l'image pour avoir l'ID)
            const exhibitionsRef = collection(db, FIRESTORE_COLLECTIONS.MUSEUMS, museum.id, FIRESTORE_COLLECTIONS.EXHIBITIONS);
            const exhibitionData = {
                name: nameTranslations,
                description: descriptionTranslations,
                cover: '',
                status: statusByLang, // Statut par langue
                order: order,
                durationTimeInMinutes: Object.fromEntries(museumLanguages.map(lang => [lang, 0])),
                episodesNumber: Object.fromEntries(museumLanguages.map(lang => [lang, 0])),
                isTemporary: false,
                color: color,
                createdAt: Object.fromEntries(museumLanguages.map(lang => [lang, new Date()])),
                updatedAt: Object.fromEntries(museumLanguages.map(lang => [lang, new Date()])),
            };

            const docRef = await addDoc(exhibitionsRef, exhibitionData);

            // Upload de l'image si elle existe, avec le bon chemin maintenant qu'on a l'ID
            if (selectedImage) {
                const imagePath = `museums/${museum.id}/exhibitions/${docRef.id}/cover_${Date.now()}_${selectedImage.name}`;
                const coverUrl = await uploadImageToStorage(selectedImage, imagePath);

                // Mettre à jour le document avec l'URL de l'image
                await updateDoc(doc(db, FIRESTORE_COLLECTIONS.MUSEUMS, museum.id, FIRESTORE_COLLECTIONS.EXHIBITIONS, docRef.id), {
                    cover: coverUrl,
                    coverAlt: buildImageAlt({ imageAlt: coverAltByLang, activeLanguages, hasImage: true }),
                });
            }

            // Rediriger vers la page de détail de l'exposition
            const slug = museumSlugParam || museumSlug;
            if (slug) {
                navigate(`/${slug}/exhibitions/${docRef.id}`, { replace: true });
            } else {
                navigate(`../${docRef.id}`, { replace: true });
            }
        } catch (error) {
            logger.error('Erreur lors de la création de l\'exposition:', error);
            showError(handleFirebaseError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <div className="create-exhibition-page">
            <button className="back-button" onClick={() => navigate(-1)}>
                <img src={arrowIcon} alt="Retour" className="chevron-icon" />
                <span>{t('common.back')}</span>
            </button>

            <LanguageTabs
                activeLanguages={activeLanguages}
                currentLanguage={currentLanguage}
                onLanguageChange={setCurrentLanguage}
                onAddLanguage={() => {
                    const available = museumLanguages.filter((lang) => !activeLanguages.includes(lang));
                    if (available.length > 0) {
                        setSelectedLanguage(available[0]);
                    }
                    setLanguageModalOpen(true);
                }}
            />

            <LanguageModal
                isOpen={languageModalOpen}
                onClose={() => setLanguageModalOpen(false)}
                selectedLanguage={selectedLanguage}
                onLanguageSelect={setSelectedLanguage}
                activeLanguages={activeLanguages}
                museumLanguages={museumLanguages}
                onAddLanguage={(lang) => {
                    if (!activeLanguages.includes(lang)) {
                        // Ajouter la langue et trier pour que 'fr' soit toujours en premier
                        const newLanguages = sortLanguagesWithFrFirst([...activeLanguages, lang]);
                        setActiveLanguages(newLanguages);
                        setCurrentLanguage(lang);
                        if (!translations[lang]) {
                            setTranslations({
                                ...translations,
                                [lang]: { title: '', description: '' },
                            });
                        }
                    }
                    setLanguageModalOpen(false);
                }}
                languageDropdownOpen={languageDropdownOpen}
                onLanguageDropdownToggle={() => setLanguageDropdownOpen(!languageDropdownOpen)}
            />

            <div className="exhibition-form-card">
                <ExhibitionFormFields
                    formLanguage={currentLanguage}
                    status={statusByLanguage[currentLanguage] || 'draft'}
                    onStatusChange={(newStatus) => {
                        setStatusByLanguage({
                            ...statusByLanguage,
                            [currentLanguage]: newStatus,
                        });
                    }}
                    color={color}
                    onColorChange={setColor}
                    order={order}
                    onOrderChange={setOrder}
                    selectedImage={selectedImage}
                    imagePreview={imagePreview}
                    onImageSelect={handleImageSelect}
                    onImageDelete={handleImageDelete}
                    title={translations[currentLanguage]?.title || ''}
                    onTitleChange={(title) => {
                        setTranslations({
                            ...translations,
                            [currentLanguage]: {
                                ...translations[currentLanguage],
                                title,
                            },
                        });
                    }}
                    description={translations[currentLanguage]?.description || ''}
                    onDescriptionChange={(description) => {
                        setTranslations({
                            ...translations,
                            [currentLanguage]: {
                                ...translations[currentLanguage],
                                description,
                            },
                        });
                    }}
                    coverAlt={translations[currentLanguage]?.coverAlt || ''}
                    onCoverAltChange={(coverAlt) => {
                        setTranslations({
                            ...translations,
                            [currentLanguage]: { ...translations[currentLanguage], coverAlt },
                        });
                    }}
                    statusDropdownOpen={statusDropdownOpen}
                    onStatusDropdownToggle={() => {
                        setStatusDropdownOpen(!statusDropdownOpen);
                        setColorDropdownOpen(false);
                        setOrderDropdownOpen(false);
                    }}
                    colorDropdownOpen={colorDropdownOpen}
                    onColorDropdownToggle={() => {
                        setColorDropdownOpen(!colorDropdownOpen);
                        setStatusDropdownOpen(false);
                        setOrderDropdownOpen(false);
                    }}
                    orderDropdownOpen={orderDropdownOpen}
                    onOrderDropdownToggle={() => {
                        setOrderDropdownOpen(!orderDropdownOpen);
                        setStatusDropdownOpen(false);
                        setColorDropdownOpen(false);
                    }}
                    colorMap={exhibitionColorMap}
                    availableColors={effectiveAvailableColors}
                    totalCount={(allExhibitions.length || 0) + 1} // +1 car on crée une nouvelle expo
                    onSave={handleSaveDraft}
                    onCancel={handleCancel}
                    saving={loading}
                />
            </div>
        </div>
    );
};

